import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import classnames from "classnames";
import {
  ForwardedRef,
  forwardRef,
  JSXElementConstructor,
  memo,
  ReactNode,
  useState,
} from "react";
import CircleSpinner from "../../circle-spinner.svg";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextColorValue, TextWeightValue } from "../../theme/typography";
import { OptionalTooltip } from "../../tooltip/optional-tooltip";
import { OverflowTooltip } from "../../tooltip/overflow-tooltip";
import * as redoFilterGroupCss from "./redo-filter-group.module.css";

/**
 * Query and value are designed to be supplied with components like
 * `RedoFilterDropdownAnchor` and `FilterSection`.
 */
export type RedoFilterGroupSize = "sm" | "lg";

interface RedoFilterGroupProps {
  propertyName: string;
  /** Replaces the plain property-name label, e.g. a clickable dropdown anchor. */
  propertyNameSlot?: ReactNode;
  Icon: JSXElementConstructor<any>;
  query: ReactNode;
  value: ReactNode;
  removeFilter?: () => void;
  size?: RedoFilterGroupSize;
  "data-arbiter"?: string;
}

export const RedoFilterGroup = memo(function RedoFilterGroup({
  propertyName,
  propertyNameSlot,
  Icon,
  query,
  value,
  removeFilter,
  size = "sm",
  "data-arbiter": dataArbiter = "RedoFilterGroup",
}: RedoFilterGroupProps) {
  const { t } = useTranslation();
  const isLg = size === "lg";
  const [propertyOverflowRef, setPropertyOverflowRef] =
    useState<HTMLElement | null>(null);
  return (
    <Flex
      className={classnames(
        redoFilterGroupCss.boxItems,
        isLg && redoFilterGroupCss.boxItemsLg,
      )}
      data-arbiter={dataArbiter}
      gap="none"
      textColor="primary"
      w="fit-content"
    >
      <Flex align="center" px={isLg ? "lg" : "md"} py={isLg ? "sm" : "xxs"}>
        <Flex
          className={classnames(
            redoFilterGroupCss.iconWrapper,
            isLg && redoFilterGroupCss.iconWrapperLg,
          )}
        >
          <Icon className={redoFilterGroupCss.icon} />
        </Flex>
        {propertyNameSlot ?? (
          <OverflowTooltip
            overflowRef={propertyOverflowRef}
            tooltipProps={{ title: propertyName }}
          >
            <Text
              className={redoFilterGroupCss.truncatedLabel}
              fontSize={isLg ? "md" : "sm"}
              fontWeight="medium"
              overflow="hidden"
              ref={setPropertyOverflowRef}
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {propertyName}
            </Text>
          </OverflowTooltip>
        )}
      </Flex>

      {query}
      {value}
      {removeFilter && (
        <button
          aria-label={t("Remove filter: {{propertyName}}", { propertyName })}
          className={classnames(
            redoFilterGroupCss.button,
            isLg && redoFilterGroupCss.buttonLg,
          )}
          onClick={removeFilter}
        >
          <Flex
            className={classnames(
              redoFilterGroupCss.iconWrapper,
              isLg && redoFilterGroupCss.iconWrapperLg,
            )}
          >
            <XIcon />
          </Flex>
        </button>
      )}
    </Flex>
  );
});

/**
 * Forward the ref, and supply it to a dropdown that applies the on click
 */
export const RedoFilterDropdownAnchor = memo(
  forwardRef(function RedoFilterGroupButton(
    {
      text,
      color = "primary",
      weight,
      tooltip,
      onClick,
      IconTrailing,
      className,
      hoverless = false,
    }: {
      text: string;
      color?: TextColorValue;
      weight?: TextWeightValue;
      tooltip?: ReactNode;
      onClick?(): void;
      IconTrailing?: JSXElementConstructor<any>;
      className?: string;
      /** Drops the hover/active background so the anchor reads as a plain label. */
      hoverless?: boolean;
    },
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    const [overflowRef, setOverflowRef] = useState<HTMLElement | null>(null);
    const label = (
      <Text
        className={redoFilterGroupCss.truncatedLabel}
        fontSize="sm"
        fontWeight={weight}
        overflow="hidden"
        ref={setOverflowRef}
        textColor={color}
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {text}
      </Text>
    );
    return (
      <OptionalTooltip
        meetsCondition={!!tooltip}
        tooltipProps={{ title: tooltip, placement: "top" }}
      >
        <button
          className={classnames(
            redoFilterGroupCss.button,
            hoverless && redoFilterGroupCss.buttonHoverless,
            className,
          )}
          data-arbiter="RedoFilterDropdownAnchor"
          onClick={onClick}
          ref={ref}
        >
          <Flex align="center" gap="sm" pt={IconTrailing ? "xs" : undefined}>
            {tooltip ? (
              label
            ) : (
              <OverflowTooltip
                overflowRef={overflowRef}
                tooltipProps={{ title: text }}
              >
                {label}
              </OverflowTooltip>
            )}
            {IconTrailing && (
              <Flex
                className={classnames(
                  redoFilterGroupCss.iconWrapper,
                  redoFilterGroupCss.iconSecondary,
                )}
              >
                <IconTrailing />
              </Flex>
            )}
          </Flex>
        </button>
      </OptionalTooltip>
    );
  }),
);

export const RedoFilterSection = memo(function FilterSection({
  text,
  color = "primary",
  weight,
  pending = false,
}: {
  text?: string;
  color?: TextColorValue;
  weight?: TextWeightValue;
  pending?: boolean;
}) {
  const [overflowRef, setOverflowRef] = useState<HTMLElement | null>(null);
  return (
    <Flex align="center" data-arbiter="RedoFilterSection" px="md" py="xxs">
      {pending && (
        <Flex className={redoFilterGroupCss.iconWrapper}>
          <CircleSpinner />
        </Flex>
      )}
      {text && (
        <OverflowTooltip
          overflowRef={overflowRef}
          tooltipProps={{ title: text }}
        >
          <Text
            className={redoFilterGroupCss.truncatedLabel}
            fontSize="sm"
            fontWeight={weight}
            overflow="hidden"
            ref={setOverflowRef}
            textColor={color}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {text}
          </Text>
        </OverflowTooltip>
      )}
    </Flex>
  );
});
